import { Component, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { AppSettings } from '../app.settings';

import { Donation, DonationType } from '../transaction-store/donation';
import { PaymentInfoActions, PaymentInfo } from '../transaction-store/payment-info';
import { DonationActions } from '../transaction-store/donation';
import { UserModel } from '../user/login.model';
import { AuthService } from '../api/auth.service';
import { GaClient } from '../../init';
import { Recurring, Frequency } from '../transaction-store/recurring';
import { Sdk } from '../core/sdk.service';
import { PaymentGateway, PaymentMethodType } from '@idonatedev/idonate-sdk';

@Component({
  selector: 'app-payment-info',
  template: `
    <!-- future TODO: move switchCases into individual components, DRY up payment-title without creating a tangled mess -->
    <ng-container [ngSwitch]="tokenStrategy" *ngIf="tokenStrategy !== 'paypal'">
      <!-- the whole container is hidden for paypal, but I think it still deserves an explicit entry -->
      <ng-template *ngSwitchCase="'paypal'"></ng-template>

      <!-- Unified credit card component for both Spreedly and CardConnect -->
      <section *ngIf="isCardPayment(tokenStrategy)">
        <div class="container">
          <div class="row">
            <div class="col">
              <h2 class="section-title payment-title">
                Payment Info
              </h2>
              <cc-indicators [type]="cardType"></cc-indicators>
            </div>
          </div>
        </div>
        <div class="container">
          <payment-method paymentType="credit_card"></payment-method>
        </div>
      </section>

      <!-- Unified bank account component for both Spreedly and CardConnect -->
      <section *ngIf="isBankPayment(tokenStrategy)">
        <echeck-help></echeck-help>
        <div class="container">
          <div class="row">
            <div class="col">
              <h2 class="section-title payment-title">
                Payment Info
              </h2>
            </div>
          </div>
        </div>
        <div class="container">
          <payment-method paymentType="bank_account"></payment-method>
        </div>
      </section>

      <!-- cardconnect/googlePay -->
      <section *ngSwitchCase="'card_connect:google_pay'">
        <div class="container">
          <div class="row">
            <div class="col">
              <h2 class="section-title payment-title">
                Payment Info
              </h2>
            </div>
          </div>
        </div>
        <div class="container">
          <google-pay></google-pay>
        </div>
      </section>

      <!-- stored payment methods (switch back to one of the above if 'add new'?) -->
      <section *ngIf="showStoredMethods()">
        <div *ngIf="tokenStrategy === 'payment_method'" class="container">
          <!-- this section's title is only shown if payment_method is the current strategy -->
          <div class="row">
            <div class="col">
              <h2 class="section-title payment-title">
                Payment Info
              </h2>
            </div>
          </div>
        </div>
        <div class="container">
          <div class="collapsed-form">
            <select-field
              class="col-12"
              label="Payment Method"
              [items]="paymentMethods"
              (blur)="onMethodBlur($event)"
              [required]="true"
            >
            </select-field>
          </div>
        </div>
      </section>

      <section
        *ngIf="showRetain()"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-sm-8 text-center-sm">
              <div class="checkbox-container">
                <div
                  [attr.aria-label]="cardRetain ? 'checked' : 'unchecked'"
                  class="icon icon-inline"
                  [ngClass]="cardRetain ? 'icon-checked' : 'icon-uncheck'"
                  (click)="onRetainBlur()"
                ></div>
                <label>Retain payment method</label>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ng-container>
  `
})
export class PaymentInfoComponent implements OnInit {
  @Select() donation$: Observable<Donation>;
  @Select() user$: Observable<UserModel>;
  @Select() recurring$: Observable<Recurring>;
  @Select() paymentInfo$: Observable<PaymentInfo>;
  Frequency = Frequency;
  infoType = null;
  recurringFrequency = null;
  loggedIn = false;
  cardRetain = false;
  cardType = '';
  paymentMethods = [{text: 'Add New', val: 'new'}];
  tokenStrategy: string;
  donationType: DonationType;
  enabledTypes: string[];

  constructor(
    private auth: AuthService,
    private store: Store,
    private settings: AppSettings,
    private sdk: Sdk
  ) {
    this.donation$.subscribe(donation => {
      this.donationType = donation.type;
      this.enabledTypes = donation.enabledTypes.map(t => t.value);
      if (donation.type !== this.infoType) {
        this.infoType = donation.type;
        this.resetStrategy();
      }
    });
    this.recurring$.subscribe(recurring => {
      this.recurringFrequency = recurring.frequency;
    });
    this.paymentInfo$.subscribe(paymentInfo => {
      this.cardType = paymentInfo.cardType;
    });
  }

  ngOnInit() {
    this.resetStrategy();

    this.user$.subscribe((user: any) => {
      if (user && user.session_key) {
        GaClient.setVal('userId', user.donor_id);
        this.loggedIn = true;

        this.tokenStrategy = 'payment_method';

        this.auth.getPaymentMethods().subscribe(data => {
          const methods = data['result'].payment_methods;
          this.setPaymentMethods(methods);
        });
      } else {
        this.loggedIn = false;
        this.setPaymentMethods();
      }
    });
  }

  private resetStrategy() {
    let currentGateway: PaymentGateway = this.settings.getValue(
      'primaryGateway'
    );
    let paymentType: PaymentMethodType;

    switch (this.infoType) {
      case 'credit':
      case 'debit':
        paymentType = 'credit_card';
        break;
      case 'echeck':
        paymentType = 'bank_account';
        break;
      case 'apple_pay':
      case 'google_pay':
        paymentType = this.infoType;
        currentGateway = this.settings.getValue('digitalWalletGateway');
        break;
      case 'paypal':
        paymentType = this.infoType;
        break;
      default:
        throw new Error(`Unhandled infoType: ${this.infoType}`);
    }

    const newTokenStrategy = `${currentGateway.backendName}:${paymentType}`;

    // run some code only if strategy is changing
    if (newTokenStrategy !== this.tokenStrategy) {
      // Tokenizer initialization is now handled by the payment-method component

      this.tokenStrategy = newTokenStrategy;
    }
  }

  setPaymentMethods(methods: any[] = []) {
    this.paymentMethods = [{text: 'Add New', val: 'new'}];

    // automatically switch mode to 'Add New' if no payment methods are found
    if (methods.length === 0) {
      this.resetStrategy();
      this.store.dispatch(new PaymentInfoActions.SetMethodAdd(true));
      return;
    }

    // sort methods by stored expiration date
    methods.sort((a: any, b: any) => {
      if (a.exp && b.exp) {
        const aDate = new Date(a.exp).getTime();
        const bDate = new Date(b.exp).getTime();
        return aDate > bDate ? 1 : aDate === bDate ? 0 : -1;
      } else {
        return -1;
      }
    });

    // filter out payment methods that aren't marked valid, aren't compatible
    // with the current gateway backend, and add an asterisk to defaults
    const primaryGateway = this.settings.getValue('primaryGateway');
    const primaryBackend = primaryGateway && primaryGateway.backendName;

    for (const m of methods) {
      if (m.is_valid && this.typeEnabled(m.type)) {
        // skip methods from a different payment backend (fail-open if either is missing)
        if (primaryBackend && m.backend_name && m.backend_name !== primaryBackend) {
          continue;
        }
        this.paymentMethods.push({
          text: m.name + (m.is_default ? ' *' : ''),
          val: m
        });
      }
    }

    // if all saved methods were filtered out, auto-switch to "Add New" mode
    if (this.paymentMethods.length === 1) {
      this.resetStrategy();
      this.store.dispatch(new PaymentInfoActions.SetMethodAdd(true));
    }
  }

  typeEnabled(paymentType) {
    switch (paymentType) {
      case 'credit_card':
        return this.enabledTypes.includes('credit') || this.enabledTypes.includes('debit');
      case 'bank_account':
        return this.enabledTypes.includes('echeck');
      default:
        // google pay and apple pay aren't 'saved', but we don't show the saved methods
        // component while one of those is selected.
        return true;
    }
  }

  onMethodBlur($event) {
    const isNewMethod = $event === 'new';

    if (isNewMethod) {
      this.store.dispatch(new PaymentInfoActions.SetMethod(undefined));
      this.store.dispatch(new PaymentInfoActions.SetMethodAdd(true));
      this.resetStrategy();
      return;
    }

    this.store.dispatch(new PaymentInfoActions.SetMethod($event.id));
    this.store.dispatch(new PaymentInfoActions.SetMethodAdd(false));
    this.store.dispatch(
      new DonationActions.SetDonationType(
        ['credit', 'credit_card'].includes($event.type)
          ? DonationType.Credit
          : DonationType.Check
      )
    );

    this.tokenStrategy = 'payment_method';
  }

  onRetainBlur() {
    this.cardRetain = !this.cardRetain;
    this.store.dispatch(new PaymentInfoActions.SetCardRetain(this.cardRetain));
  }

  // if logged in, not using an existing payment method, and not creating
  // a recurring donation (where retaining is forced), give the donor the
  // option to retain the new payment method.
  // Always hidden for apple_pay, google_pay
  showRetain() {
    if (this.donationType === DonationType.ApplePay || this.donationType === DonationType.GooglePay) {
      return false;
    }

    return (
      this.tokenStrategy !== 'payment_method' &&
      this.loggedIn &&
      this.recurringFrequency === Frequency.Once
    );
  }

  showStoredMethods() {
    if (this.donationType === DonationType.ApplePay || this.donationType === DonationType.GooglePay) {
      return false;
    }
    return this.loggedIn;
  }

  isCardPayment(strategy: string): boolean {
    return strategy === 'spreedly:credit_card' || strategy === 'card_connect:credit_card';
  }

  isBankPayment(strategy: string): boolean {
    return strategy === 'spreedly:bank_account' || strategy === 'card_connect:bank_account';
  }
}
